import { useMemo, useState } from 'react';
import { useData } from '../AdoptionData.jsx';
import {
  distinctPrompts, histograms, sanitizeLabels, SCIENTIFIC_AREAS, USE_CASES,
} from '../../../../lib/adoptionAnalytics.js';
import { AREA_COLOR, USE_COLOR } from '../util.js';
import { HBars } from '../charts.jsx';

const heat = (v) => {
  if (v <= 0) return '#161a22';
  const t = Math.min(1, v);
  const r = Math.round(29 + (91 - 29) * t), g = Math.round(33 + (141 - 33) * t), b = Math.round(48 + (239 - 48) * t);
  return `rgb(${r},${g},${b})`;
};

function Matrix({ companies, compMap, order, compTotal }) {
  return (
    <div className="matrix">
      <table>
        <thead>
          <tr><th className="l">Company</th><th>n</th>{order.map((o) => <th key={o} title={o}>{o.replace(/ &.*/, '').slice(0, 16)}</th>)}</tr>
        </thead>
        <tbody>
          {companies.map((c) => {
            const denom = compTotal[c] || 0;
            return (
              <tr key={c}>
                <td className="l" title={c}>{c}</td>
                <td className="dim">{denom}</td>
                {order.map((o) => {
                  const n = (compMap[c] || {})[o] || 0;
                  const share = denom ? n / denom : 0;
                  return <td key={o} className="cell" style={{ background: heat(share) }} title={`${c} — ${o}: ${n} (${Math.round(share * 100)}%)`}>{n ? Math.round(share * 100) : ''}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PromptCategories() {
  const { events, aliases, classMap, classState, classProgress } = useData();
  const [companyView, setCompanyView] = useState('__ALL__');

  const prompts = useMemo(() => distinctPrompts(events, aliases), [events, aliases]);
  const classified = useMemo(() => prompts.map((p) => {
    const c = classMap[p.text] || {};
    return {
      company: p.company,
      areas: sanitizeLabels(c.areas, SCIENTIFIC_AREAS, 'Unspecified/General'),
      uses: sanitizeLabels(c.uses, USE_CASES, 'Other'),
    };
  }), [prompts, classMap]);

  const hasClass = prompts.some((p) => classMap[p.text]);
  const hist = useMemo(() => histograms(classified), [classified]);

  const companies = useMemo(
    () => Object.keys(hist.compTotal).sort((a, b) => hist.compTotal[b] - hist.compTotal[a]),
    [hist]
  );
  const areaOrder = useMemo(() => [...SCIENTIFIC_AREAS].sort((a, b) => (hist.aggArea[b] || 0) - (hist.aggArea[a] || 0)), [hist]);
  const useOrder = useMemo(() => [...USE_CASES].sort((a, b) => (hist.aggUse[b] || 0) - (hist.aggUse[a] || 0)), [hist]);

  if (!hasClass) {
    let msg;
    if (!prompts.length) {
      msg = <div className="status">No prompts in this window.</div>;
    } else if (classState === 'loading') {
      const pct = Math.round((classProgress || 0) * 100);
      msg = <div className="status">Classifying prompts… {pct}% — you can leave this tab; it'll fill in.</div>;
    } else if (classState === 'error') {
      msg = <div className="status warn">Couldn't classify prompts — try reloading.</div>;
    } else {
      msg = <div className="status">No classifications for this window yet.</div>;
    }
    return (
      <>
        <h1>Prompt categories</h1>
        {msg}
      </>
    );
  }

  const view = companyView === '__ALL__'
    ? { area: hist.aggArea, use: hist.aggUse, denom: prompts.length, label: `all ${prompts.length} prompts` }
    : { area: hist.compArea[companyView] || {}, use: hist.compUse[companyView] || {}, denom: hist.compTotal[companyView] || 0, label: `${companyView} · ${hist.compTotal[companyView] || 0} prompts` };

  return (
    <>
      <h1>Prompt categories — scientific area &amp; use case</h1>
      <p className="desc">Multi-label classification: a prompt can span several areas/use cases, so bars sum to more than the prompt count.</p>

      <section className="card">
        <div className="card-head">
          <h2>Histograms</h2>
          <select value={companyView} onChange={(e) => setCompanyView(e.target.value)}>
            <option value="__ALL__">All companies (aggregate)</option>
            {companies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <p className="desc">Showing {view.label}.</p>
        <div className="grid2">
          <div><h3>Scientific area</h3><HBars counts={view.area} order={areaOrder} color={AREA_COLOR} denom={view.denom} /></div>
          <div><h3>Use case</h3><HBars counts={view.use} order={useOrder} color={USE_COLOR} denom={view.denom} /></div>
        </div>
      </section>

      <section className="card">
        <h2>Scientific area mix by company <span className="dim">— % of company's prompts (multi-label; rows can exceed 100%)</span></h2>
        <Matrix companies={companies} compMap={hist.compArea} order={areaOrder} compTotal={hist.compTotal} />
      </section>
      <section className="card">
        <h2>Use case mix by company <span className="dim">— % of company's prompts (multi-label; rows can exceed 100%)</span></h2>
        <Matrix companies={companies} compMap={hist.compUse} order={useOrder} compTotal={hist.compTotal} />
      </section>
    </>
  );
}
